/**
 * IR-pipeline adapter — drop-in replacement for `layoutDAG()`.
 *
 * Step 8.1 of the IR build (see docs/IR_RULE_MAPPING.md §7). The new
 * pipeline lives in utils/ir/ and operates on a typed IR tree; the legacy
 * callers (`CanvasPage.jsx`, `useCanvas.js`) speak the flat
 * `{nodes, connections}` / `(nodes, connections) → positionedNodes` shape.
 * This module bridges the two without any React or DOM dependencies so
 * the legacy sites can switch behind a feature flag with a one-line diff.
 *
 * ALGORITHM
 * ─────────
 *   1. Normalize the incoming connection shape (legacy uses {from,to,fromPort};
 *      IR expects {source,target,sourceHandle}).
 *   2. flatToIR → layoutIR → irToFlat.
 *   3. Build an id → {x,y,w,h} lookup from the laid-out flat nodes.
 *   4. Return a new array mirroring the input `nodes` (order preserved)
 *      with {x,y} stamped on from the lookup. Nodes the IR pipeline doesn't
 *      emit (e.g. a synthesized convergence pill has no pre-image in the
 *      caller's node list) are reported via the onExtra callback for the
 *      caller to append if it wants to show them.
 *
 * FALLBACK
 * ────────
 * IR is stricter than Sugiyama: it requires a Start sentinel, well-formed
 * containers, and matched fork/join pairs. Malformed workflows will throw
 * from flatToIR. The adapter catches any throw and returns `null`; the
 * caller is expected to fall back to legacy layoutDAG. This keeps the swap
 * safe for in-progress designs the user is still editing.
 *
 * WHAT IT DOES NOT DO
 * ───────────────────
 *  - It does not mutate the input arrays.
 *  - It does not emit routed edge geometry. `routeEdges(flat)` is available
 *    and will be wired in when the renderer is ready to consume `pathD`.
 *
 * HEIGHT-FEEDBACK SECOND PASS (step 8.4)
 * ──────────────────────────────────────
 * After React mounts the first pass, leaf cards render at their actual CSS
 * heights (varies with label length, config summary, pill count, etc.).
 * Callers capture a `heightMap` keyed by node id via refs and feed it back
 * through `relayoutDAGviaIR(laid, heightMap, inputNodes)` to get a fresh
 * positioned array. To enable this, `layoutDAGviaIR` exposes the laid-out
 * IR tree via `opts.onLaid(laid)` — the caller stores it and uses it as
 * the re-layout anchor.
 */

import { flatToIR } from './ir/flatToIR';
import { layoutIR } from './ir/layoutIR';
import { irToFlat } from './ir/irToFlat';
import { relayout as relayoutIRTree, bucketHeight } from './ir/relayout';
import { buildInitialHeightMap } from './nodeAnatomy';

// ───────────────────────────────────────────────────────────────────
//  Connection-shape normalization
// ───────────────────────────────────────────────────────────────────

/**
 * Convert a legacy `{from, to, fromPort}` connection to the IR-canonical
 * `{source, target, sourceHandle}` shape. Passes through connections that
 * already use the canonical shape.
 *
 * @param {object} c
 * @returns {{source: string, target: string, sourceHandle: string}}
 */
function normalizeConnection(c) {
  const source = c.source != null ? c.source : c.from;
  const target = c.target != null ? c.target : c.to;
  const sourceHandle = c.sourceHandle != null
    ? c.sourceHandle
    : (c.fromPort != null ? c.fromPort : 'default');
  return { source, target, sourceHandle };
}

// ───────────────────────────────────────────────────────────────────
//  Public API
// ───────────────────────────────────────────────────────────────────

/**
 * Run the IR pipeline and return positioned nodes.
 *
 * Drop-in alternative to `layoutDAG(nodes, connections, opts)` from
 * `utils/dagLayout.js`. Returns `null` on any IR-pipeline error so the
 * caller can fall back cleanly.
 *
 * @param {Array<{id: string, type?: string, [k: string]: unknown}>} nodes
 * @param {Array<object>} connections              Either {from,to,fromPort} or
 *                                                  {source,target,sourceHandle}.
 * @param {object} [opts]
 * @param {(extraNodes: object[]) => void} [opts.onExtraNodes]  Receives the
 *     synthesized IR nodes (e.g. Switch convergence pills) that don't appear
 *     in the input `nodes` list. Useful when the caller wants to merge them
 *     into state.
 * @param {(laid: object) => void} [opts.onLaid]  Receives the laid-out IR
 *     wrapper `{start, sequence, end, bbox}`. Callers that want to run the
 *     height-feedback second pass (step 8.4) must capture this and pass it
 *     into `relayoutDAGviaIR(laid, heightMap, nodes)` after the DOM renders.
 * @param {Object.<string, number>} [opts.initialHeightMap]
 *     Seed heights for the first pass (Phase R1). Defaults to a per-task-type
 *     map derived from `buildInitialHeightMap(nodes)`, which applies Rule S-G
 *     bucketed heights by node type. Pass `null` to opt out and fall back to
 *     the IR's default leaf-height constant. Pass a custom object to override
 *     for specific nodes — useful for tests and for callers that have
 *     better-than-default height knowledge (e.g. cached DOM measurements).
 * @returns {Array<object>|null}
 */
export function layoutDAGviaIR(nodes, connections, opts = {}) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const normalizedConnections = Array.isArray(connections)
    ? connections.map(normalizeConnection)
    : [];

  // Seed the first pass with per-task-type heights (Rule S-G). Without this,
  // every leaf is sized as if it were a 100px SIMPLE task, so containers
  // shrink too tight and subsequent DOM feedback has to reshuffle everything.
  // Callers can opt out with `initialHeightMap: null`.
  const initialHeightMap = opts.initialHeightMap === null
    ? null
    : (opts.initialHeightMap || buildInitialHeightMap(nodes));

  let flat;
  let laid;
  try {
    const canvas = { nodes, connections: normalizedConnections };
    const ir = flatToIR(canvas);
    laid = layoutIR(ir);
    // Apply per-type heights before emitting the flat geometry so containers
    // are sized correctly on the first paint. `relayoutIRTree` is idempotent
    // under bucket-matching maps, so this extra pass is free when heights
    // already match the IR defaults.
    if (initialHeightMap && Object.keys(initialHeightMap).length > 0) {
      laid = relayoutIRTree(laid, initialHeightMap);
    }
    flat = irToFlat(laid);
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[irAdapter] IR pipeline failed, returning null for legacy fallback:', err && err.message);
    }
    return null;
  }

  // Build lookup by id → absolute geometry.
  const geomById = new Map();
  for (const fn of flat.nodes) {
    geomById.set(fn.id, { x: fn.x, y: fn.y, w: fn.w, h: fn.h });
  }

  // Preserve input ordering, stamp on geometry for every node the IR
  // pipeline recognised. If the pipeline did not emit a geometry for an
  // input id (shouldn't happen for well-formed canvases) we leave x/y
  // untouched so the caller sees the unmodified node.
  const positioned = nodes.map((n) => {
    const g = geomById.get(n.id);
    return g ? { ...n, x: g.x, y: g.y, w: g.w, h: g.h } : n;
  });

  // Report synthesised nodes (e.g. `<switchId>__conv` convergence pills)
  // that the caller didn't know about. This is optional — callers that
  // don't subscribe just ignore them.
  if (typeof opts.onExtraNodes === 'function') {
    const inputIds = new Set(nodes.map((n) => n.id));
    const extras = flat.nodes.filter((fn) => !inputIds.has(fn.id));
    if (extras.length > 0) opts.onExtraNodes(extras);
  }

  // Surface the laid IR so callers can drive the height-feedback pass
  // via relayoutDAGviaIR. Cheap no-op if no one subscribes.
  if (typeof opts.onLaid === 'function') {
    opts.onLaid(laid);
  }

  return positioned;
}

// ───────────────────────────────────────────────────────────────────
//  Height-feedback second pass — step 8.4
// ───────────────────────────────────────────────────────────────────

/**
 * Re-run the layout pass with DOM-measured leaf heights. Mirrors the shape
 * of `layoutDAGviaIR` for easy drop-in from a React effect: takes the laid
 * IR captured during the first pass and a `heightMap` of measured pixel
 * heights, returns a fresh positioned-nodes array in the SAME order as the
 * input `nodes`.
 *
 * Returns `null` on any pipeline error so the caller can skip the second
 * pass and keep the first-pass geometry on screen — a missed relayout is
 * visually harmless (cards render slightly overlapped); a throw inside a
 * React effect would crash the canvas.
 *
 * IDEMPOTENCE
 * ───────────
 * `relayoutDAGviaIR(laid, map, nodes)` is idempotent under height bucketing
 * (see `relayout.js`): two consecutive calls with the same measurement map
 * produce identical geometry. Safe to call from a useEffect that compares
 * the last-applied heightMap to the new one before triggering a re-render.
 *
 * @param {{start:object, sequence:object[], end:object|null, bbox:object}} laid
 *   The laid-out IR wrapper captured from `layoutDAGviaIR`'s `onLaid` callback.
 * @param {Object.<string, number>} heightMap   id → measured CSS pixel height.
 * @param {Array<{id: string}>} nodes           Original input node list — the
 *   return value mirrors this array's order, same as layoutDAGviaIR.
 * @param {object} [opts]
 * @param {(extraNodes: object[]) => void} [opts.onExtraNodes]
 * @param {(laid: object) => void} [opts.onLaid]   Receives the NEW laid tree
 *   — callers can cache this for a potential third pass if leaf heights
 *   shift on hover/expand.
 * @returns {Array<object>|null}
 */
export function relayoutDAGviaIR(laid, heightMap, nodes, opts = {}) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  if (!laid || !laid.start || !Array.isArray(laid.sequence)) return null;

  let flat;
  let nextLaid;
  try {
    nextLaid = relayoutIRTree(laid, heightMap || {});
    flat = irToFlat(nextLaid);
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[irAdapter] height-feedback relayout failed, keeping first-pass geometry:', err && err.message);
    }
    return null;
  }

  const geomById = new Map();
  for (const fn of flat.nodes) {
    geomById.set(fn.id, { x: fn.x, y: fn.y, w: fn.w, h: fn.h });
  }

  const positioned = nodes.map((n) => {
    const g = geomById.get(n.id);
    return g ? { ...n, x: g.x, y: g.y, w: g.w, h: g.h } : n;
  });

  if (typeof opts.onExtraNodes === 'function') {
    const inputIds = new Set(nodes.map((n) => n.id));
    const extras = flat.nodes.filter((fn) => !inputIds.has(fn.id));
    if (extras.length > 0) opts.onExtraNodes(extras);
  }

  if (typeof opts.onLaid === 'function') {
    opts.onLaid(nextLaid);
  }

  return positioned;
}

// Re-export the low-level primitives so advanced callers (or future
// effect-level code) can skip the adapter and drive the pipeline directly.
export { flatToIR, layoutIR, irToFlat, bucketHeight };
